export const BILLING_CURRENCY = "EUR";

export const BILLING_INTERVALS = Object.freeze({
  MONTH: "month",
  YEAR: "year",
});

export const DEFAULT_BILLING_INTERVAL = BILLING_INTERVALS.MONTH;
export const ANNUAL_DISCOUNT_MONTHS = 2;

const DEFAULT_MIN_FRACTION_DIGITS = 0;
const DEFAULT_MAX_FRACTION_DIGITS = 0;

function toFiniteAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return numeric;
}

function createCurrencyFormatter(
  locale,
  { minimumFractionDigits = DEFAULT_MIN_FRACTION_DIGITS, maximumFractionDigits = DEFAULT_MAX_FRACTION_DIGITS } = {},
) {
  const options = {
    style: "currency",
    currency: BILLING_CURRENCY,
    minimumFractionDigits,
    maximumFractionDigits,
  };

  try {
    return new Intl.NumberFormat(locale, options);
  } catch {
    return new Intl.NumberFormat("en", options);
  }
}

function resolveCurrencySymbol(currencyCode) {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const currencyPart = parts.find((part) => part.type === "currency");
    return currencyPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
}

function roundToSingleDecimal(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.round(numeric * 10) / 10;
}

function buildPlanPricing({ monthly = 0, yearly = 0 } = {}) {
  const amountMonthly = toFiniteAmount(monthly);
  const amountYearly = toFiniteAmount(yearly);
  const annualBase = amountMonthly * 12;
  const savingsAnnual =
    annualBase > 0 && amountYearly > 0 ? Math.max(0, annualBase - amountYearly) : 0;
  const savingsPct =
    annualBase > 0 && savingsAnnual > 0
      ? roundToSingleDecimal((savingsAnnual / annualBase) * 100)
      : 0;
  const annualDiscountMonths = savingsAnnual > 0 ? ANNUAL_DISCOUNT_MONTHS : 0;
  const monthlyEquivalentYearly =
    amountYearly > 0 ? roundToSingleDecimal(amountYearly / 12) : 0;

  return Object.freeze({
    currency: BILLING_CURRENCY,
    interval: DEFAULT_BILLING_INTERVAL,
    amountMonthly,
    amountYearly,
    savingsAnnual,
    savingsPct,
    annualDiscountMonths,
    monthlyEquivalentYearly,
    intervals: Object.freeze({
      [BILLING_INTERVALS.MONTH]: Object.freeze({
        amount: amountMonthly,
        currency: BILLING_CURRENCY,
        interval: BILLING_INTERVALS.MONTH,
      }),
      [BILLING_INTERVALS.YEAR]: Object.freeze({
        amount: amountYearly,
        currency: BILLING_CURRENCY,
        interval: BILLING_INTERVALS.YEAR,
        savingsAnnual,
        savingsPct,
        annualDiscountMonths,
        monthlyEquivalent: monthlyEquivalentYearly,
      }),
    }),
  });
}

export const PRICING_CURRENCY = Object.freeze({
  code: BILLING_CURRENCY,
  symbol: resolveCurrencySymbol(BILLING_CURRENCY),
});

export const PLAN_PRICING = Object.freeze({
  free: buildPlanPricing({ monthly: 0, yearly: 0 }),
  starter: buildPlanPricing({ monthly: 12, yearly: 120 }),
  pro: buildPlanPricing({ monthly: 29, yearly: 290 }),
});

export function normalizeBillingInterval(value, fallback = DEFAULT_BILLING_INTERVAL) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === BILLING_INTERVALS.YEAR) {
    return BILLING_INTERVALS.YEAR;
  }
  if (normalized === BILLING_INTERVALS.MONTH) {
    return BILLING_INTERVALS.MONTH;
  }
  return normalizeBillingInterval(fallback, DEFAULT_BILLING_INTERVAL);
}

export function getPlanPricing(planId = "free") {
  const normalized = String(planId || "").trim().toLowerCase();
  return PLAN_PRICING[normalized] || PLAN_PRICING.free;
}

export function getPlanIntervalPricing(
  planId = "free",
  interval = DEFAULT_BILLING_INTERVAL,
) {
  const pricing = getPlanPricing(planId);
  return pricing.intervals[normalizeBillingInterval(interval)];
}

export function formatBillingPrice(
  value,
  { locale, minimumFractionDigits = DEFAULT_MIN_FRACTION_DIGITS, maximumFractionDigits = DEFAULT_MAX_FRACTION_DIGITS } = {},
) {
  const amount = toFiniteAmount(value);
  return createCurrencyFormatter(locale, { minimumFractionDigits, maximumFractionDigits }).format(amount);
}
